import { supabase } from "./supabase";

export const testDatabaseConnection = async () => {
  console.log("=== Starting Database Connection Test ===");

  const results = {
    connection: false,
    tables: {
      users: false,
      pharmacies: false,
      user_pharmacies: false,
      tasks: false,
      communications: false
    },
    errors: [] as string[]
  };

  try {
    // Test 1: Basic connection test
    console.log("Test 1: Testing basic Supabase connection...");
    const { data: testData, error: testError } = await supabase
      .from('users')
      .select('count(*)', { count: 'exact', head: true });

    if (testError) {
      console.error("Connection test failed:", testError);
      results.errors.push(`Connection test: ${testError.message}`);
    } else {
      console.log("✅ Basic connection successful");
      results.connection = true;
    }

    // Test 2: Check each table exists and is accessible
    const tables = ['users', 'pharmacies', 'user_pharmacies', 'tasks', 'communications'];

    for (const table of tables) {
      console.log(`Test 2.${tables.indexOf(table) + 1}: Checking table '${table}'...`);

      try {
        const { data, error } = await supabase
          .from(table)
          .select('*')
          .limit(1);

        if (error) {
          console.error(`❌ Table '${table}' error:`, error);
          results.errors.push(`Table ${table}: ${error.message}`);

          // Check specific error codes
          if (error.code === '42P01') {
            console.error(`   → Table '${table}' does not exist`);
          } else if (error.code === '42501') {
            console.error(`   → No permission to access table '${table}'`);
          }
        } else {
          console.log(`✅ Table '${table}' is accessible`);
          results.tables[table as keyof typeof results.tables] = true;
        }
      } catch (err) {
        console.error(`❌ Unexpected error with table '${table}':`, err);
        results.errors.push(`Table ${table}: Unexpected error`);
      }
    }

    // Test 3: Check current user
    console.log("Test 3: Checking current authenticated user...");
    const { data: { user }, error: userError } = await supabase.auth.getUser();

    if (userError) {
      console.error("❌ Auth user check failed:", userError);
      results.errors.push(`Auth check: ${userError.message}`);
    } else if (user) {
      console.log("✅ Authenticated as:", user.email);

      // Test 4: Check if user exists in users table
      console.log("Test 4: Checking if user exists in users table...");
      const { data: userData, error: userDataError } = await supabase
        .from('users')
        .select('*')
        .eq('id', user.id)
        .single();

      if (userDataError) {
        console.error("❌ User lookup failed:", userDataError);
        results.errors.push(`User lookup: ${userDataError.message}`);
      } else {
        console.log("✅ User found in database:", userData);
      }
    } else {
      console.log("⚠️  No authenticated user");
    }

    // Test 5: Test a simple query with timeout
    console.log("Test 5: Testing query with timeout...");
    const queryPromise = supabase
      .from('tasks')
      .select('id')
      .limit(1);

    const timeoutPromise = new Promise((_, reject) =>
      setTimeout(() => reject(new Error('Query timeout after 5 seconds')), 5000)
    );

    try {
      const result = await Promise.race([queryPromise, timeoutPromise]);
      console.log("✅ Query completed successfully");
    } catch (err) {
      console.error("❌ Query timed out or failed:", err);
      results.errors.push(`Query timeout test: ${err}`);
    }

  } catch (error) {
    console.error("❌ Unexpected error during tests:", error);
    results.errors.push(`Unexpected: ${error}`);
  }

  // Summary
  console.log("\n=== Test Summary ===");
  console.log("Connection:", results.connection ? "✅ Working" : "❌ Failed");
  console.log("Tables:");
  Object.entries(results.tables).forEach(([table, accessible]) => {
    console.log(`  ${table}: ${accessible ? "✅ Accessible" : "❌ Not accessible"}`);
  });

  if (results.errors.length > 0) {
    console.log("\nErrors encountered:");
    results.errors.forEach(error => console.log(`  - ${error}`));
  }

  console.log("\n=== End of Database Test ===");

  return results;
};

// Function to run from browser console
(window as any).testDatabase = testDatabaseConnection;

console.log("Database test utility loaded. Run 'testDatabase()' in console to test.");
